import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import axios from 'axios';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import Login from '../NewHome/components/Login';
import OtpModal from '../NewHome/components/OtpModal';
import SignUp from '../NewHome/components/SignUp';
import Modal from '../NewHome/components/Modal';
import Header from '../Challenge/components/Header';

import { LOCAL_STORAGE_CONSTANT, MODAL_TYPE } from '../../constants/app';
import { removeItem } from '../../utils/cache';
import { TopBar } from '../../containers/MoveToImprove/sections';
import { SEGMENT_EVENTS, trackEvent } from '../../utils/Segment';
import { Wrapper } from '../NewHome';
import {
  addAosAnimateClassObserver,
  getDisplayType,
  logOutput,
  scrollToTop,
} from '../../utils/helpers';
import useWindowWidth from '../../hooks/useWindowWidth';
import SeoTags from '../../components/SeoComponent';
import { getCanonicalLink, SeoTexts } from '../../services/SEO';
import { extractJSON } from '../../services/jsonUtil';
import { useCourseDetail } from '../../hooks/useCourseDetail';
import ErrorModal from '../../components/ErrorModal';
import { withFooter } from '../../hoc/withFooter';
import { COURSES_SLUGS } from '../../route';
import {
  FoundationDetails,
  FoundationMap,
  IsThisYou,
  JoinMobility,
  JoinPlan,
  MobilityDetails,
  VideoSection,
  // WinPrizes,
} from './components';
import Testimonial from '../FullBodyLowImpact/Testimonials';
import MeetDocJen from '../GratefulForBody/MeetDocJen';
import FAQSection from './components/FAQSection';
import {
  mobilityFoundationsFAQs,
  mobilityFoundationsTestimonials,
} from './constant';
import { docjen, docjenWebp } from './images';
import AOS from 'aos';
import 'aos/dist/aos.css';

import './style.css';

const MobilityFoundation = ({ history, match }) => {
  const [modalState, setModalState] = useState(null);
  const [countryCode, setCountryCode] = useState('us');
  const displayType = getDisplayType(useWindowWidth());
  const isMobileScreen = displayType === 'mobile';

  const {
    challengeError,
    handleCourseRedirection,
    isLoading,
    setChallengeError,
    courseDetail,
  } = useCourseDetail(history, match);

  const testimonialRef = useRef();
  const faqRef = useRef();
  const meetDocJenRef = useRef();
  useEffect(() => {
    scrollToTop();
    AOS.init({
      // Global AOS settings here
    });
    const element = testimonialRef.current;

    const observer = addAosAnimateClassObserver();

    if (element) {
      observer.observe(element);
    }

    if (faqRef?.current) {
      observer.observe(faqRef?.current);
    }
    if (meetDocJenRef?.current) {
      observer.observe(meetDocJenRef?.current);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
      if (faqRef?.current) {
        observer.unobserve(faqRef?.current);
      }
      if (meetDocJenRef?.current) {
        observer.unobserve(meetDocJenRef?.current);
      }
    };
  }, []);

  const handleModalState = modalData => setModalState(modalData);

  const handleModalEvent = modalType => setModalState(modalType);

  useEffect(() => {
    axios
      .get('https://ipapi.co/json/')
      .then(response => {
        const data = response.data;
        setCountryCode(data.country_code.toLowerCase());
      })
      .catch(error => {
        logOutput(error);
      });
  }, []);

  const dialogClassName = (() => {
    switch (modalState) {
      case MODAL_TYPE.SUCCESS:
        return 'success-modal';
      case MODAL_TYPE.WAITLIST:
        return 'survey-modal';
      default:
        return undefined;
    }
  })();

  const handleClose = () => {
    removeItem(LOCAL_STORAGE_CONSTANT.SURVEY_SESSION);
    removeItem(LOCAL_STORAGE_CONSTANT.SURVEY_STATUS);
    setModalState(false);
  };

  const handleAuthFlow = (buttonTitle, eventType, section) => {
    trackEvent(SEGMENT_EVENTS.BUTTON_CLICKED, {
      button_title: buttonTitle,
      page: 'Mobility Foundations Page',
      section_title: `${section}`,
    });
    const user = extractJSON(LOCAL_STORAGE_CONSTANT.USER);
    if (user) {
      handleCourseRedirection();
    } else {
      trackEvent(SEGMENT_EVENTS.AUTH_FLOW_INITIATED, {
        page: 'Mobility Foundations Page',
        section_title: `${section}`,
      });
      handleModalEvent(eventType);
      if (isMobileScreen) {
        scrollToTop();
      }
    }
  };

  const renderModalContent = () => {
    switch (modalState) {
      case MODAL_TYPE.LOGIN:
        return (
          <Wrapper type={modalState} handleGoBack={() => setModalState(null)}>
            <Login
              page="Mobility Foundations Page"
              countryCode={countryCode}
              handleModalState={handleModalState}
              handleModalEvent={handleModalEvent}
              plan_id={courseDetail?.id}
            />
          </Wrapper>
        );
      case MODAL_TYPE.OTP:
        return (
          <Wrapper type={modalState} handleGoBack={() => setModalState(null)}>
            <OtpModal
              page="Mobility Foundations Page"
              handleModalState={handleModalState}
              handleModalEvent={handleModalEvent}
              plan_id={courseDetail?.id}
            />
          </Wrapper>
        );
      case MODAL_TYPE.SIGNUP:
        return (
          <Wrapper type={modalState} handleGoBack={() => setModalState(null)}>
            <SignUp
              page="Mobility Foundations Page"
              countryCode={countryCode}
              handleModalState={handleModalState}
              handleModalEvent={handleModalEvent}
              plan_id={courseDetail?.id}
            />
          </Wrapper>
        );
      default:
        return null;
    }
  };

  const renderTopBar = () => {
    const currentDate = dayjs();
    const earlyBidEndDate = dayjs('2023-10-27T23:59:59');
    const extendedEarlyBidEndDate = dayjs('2023-10-29T23:59:59');
    const finalEndDate = dayjs('2023-11-05T23:59:59');
    const endDate =
      currentDate > extendedEarlyBidEndDate
        ? finalEndDate
        : currentDate > earlyBidEndDate
        ? extendedEarlyBidEndDate
        : earlyBidEndDate;

    let text;

    if (currentDate < earlyBidEndDate) {
      text = 'Early Bird Sale Ends Friday, October 27th!';
    } else if (currentDate < extendedEarlyBidEndDate) {
      text = 'Early Bird Sale Extended Ends Sunday, October 29th!';
    } else if (
      currentDate > extendedEarlyBidEndDate &&
      currentDate < finalEndDate
    ) {
      text = 'Course starts Sunday, November 5th!';
    } else {
      text = 'Signup and get started on Self-Assessments now!';
    }
    const description = 'Signup and get started on Self-Assessments now!';

    return (
      <TopBar
        description={description}
        endDate={endDate.toString()}
        loading={isLoading}
        onClick={handleAuthFlow}
        text={text}
        hideTimeCounter={true}
      />
    );
  };

  const currentPrice = useMemo(() => {
    return courseDetail?.attributes?.amount
      ? +courseDetail?.attributes?.amount / 100
      : 0;
  }, [courseDetail?.attributes?.amount]);

  const renderPageContent = () => (
    <div className="mobility-foundation-page position-relative">
      {/* <Label onClick={handleAuthFlow} endDate={new Date("2023-08-21T00:00:00.000")} text={TEXT_CONSTANT.REGISTER_FOR_PELVLIC_FLOOR} /> */}
      {renderTopBar()}
      <Header showJoinNowBtn onClick={handleAuthFlow} />
      <JoinPlan
        courseDetail={courseDetail}
        isLoading={isLoading}
        onClick={handleAuthFlow}
        currentPrice={currentPrice}
      />
      <VideoSection isLoading={isLoading} onClick={handleAuthFlow} />
      <MobilityDetails isLoading={isLoading} onClick={handleAuthFlow} />
      <div
        ref={testimonialRef}
        data-aos="fade-in"
        data-aos-delay="50"
        data-aos-duration="1000"
        data-aos-easing="ease-in-out"
      >
        <Testimonial
          headerText="Testimonials"
          testimonials={mobilityFoundationsTestimonials}
          isCarouselAlways
        />
      </div>
      <JoinMobility
        courseDetail={courseDetail}
        isLoading={isLoading}
        onClick={handleAuthFlow}
        currentPrice={currentPrice}
      />
      {/* <WinPrizes loading={isLoading} onClick={handleAuthFlow} /> */}
      <IsThisYou />
      <FoundationDetails
        courseDetail={courseDetail}
        isLoading={isLoading}
        onClick={handleAuthFlow}
        currentPrice={currentPrice}
      />
      <FoundationMap isLoading={isLoading} onClick={handleAuthFlow} />
      <div
        ref={faqRef}
        data-aos="fade-up"
        data-aos-delay="50"
        data-aos-duration="1000"
        data-aos-easing="ease-in-out"
      >
        <FAQSection onClick={handleAuthFlow} faqs={mobilityFoundationsFAQs} />
      </div>
      <div
        ref={meetDocJenRef}
        data-aos="fade-up"
        data-aos-delay="50"
        data-aos-duration="1000"
        data-aos-easing="ease-in-out"
      >
        <MeetDocJen
          isBackgroundDiv
          backColor="#334C4C"
          imgSrcSet={{
            errorSrc: docjen,
            placeholderSrc: docjenWebp,
            src: docjenWebp,
          }}
        />
      </div>
    </div>
  );

  const renderContentMobile = () => {
    if (modalState) {
      return renderModalContent();
    } else {
      return renderPageContent();
    }
  };

  return (
    <div className={clsx('main', isMobileScreen && modalState && 'h-100')}>
      {isMobileScreen &&
      ![MODAL_TYPE.SUCCESS, MODAL_TYPE.WAITLIST].includes(modalState)
        ? renderContentMobile()
        : renderPageContent()}
      {!isMobileScreen && (
        <Modal
          dialogClassName={dialogClassName}
          show={Boolean(modalState)}
          handleClose={handleClose}
        >
          {renderModalContent()}
        </Modal>
      )}
      <ErrorModal
        handleClose={() => setChallengeError(false)}
        handleCourseRedirection={handleCourseRedirection}
        isLoading={isLoading}
        page="Mobility Foundations Page"
        show={challengeError}
      />
      <SeoTags
        title={SeoTexts.MOBILITY_FOUNDATIONS_TITLE}
        metaDescription={SeoTexts.MOBILITY_FOUNDATIONS_DESCRIPTION}
        canonicalLink={getCanonicalLink(
          `/${COURSES_SLUGS.MOBILITY_FOUNDATIONS}`,
        )}
        ogImgUrl={SeoTexts.MOBILITY_FOUNDATIONS_OG_IMAGE_URL}
        twitterImgUrl={SeoTexts.MOBILITY_FOUNDATIONS_TWITTER_IMAGE_URL}
      />
    </div>
  );
};

MobilityFoundation.propTypes = {
  history: PropTypes.object,
  match: PropTypes.object,
};

export default withFooter(MobilityFoundation);
